/**
 * Mock-«сервер» новостей: ведёт себя как будущие ручки из `contract.ts`, но
 * живёт в localStorage браузера — временно, пока бэкенд их не сделал. У
 * админки и приложения наборы разные (разные домены): созданное в админке
 * приложение увидит только с настоящим API.
 *
 * Проверки повторяют серверные и отвечают тем же `ApiError`.
 */
import { ApiError } from '../../errors'
import { toDataUrl } from '../../stories/mocks/store'
import {
  NEWS_BODY_MAX,
  NEWS_LEAD_MAX,
  NEWS_MEDIA_MAX_BYTES,
  NEWS_PAGE_SIZE,
  NEWS_TITLE_MAX,
  type NewsDto,
  type NewsInput,
  type NewsMediaDto,
  type NewsPage,
  type PublicNewsDto,
  type PublicNewsItemDto,
} from '../contract'
import { SEED_NEWS } from './seed'

export interface NewsQuery {
  page?: number
  pageSize?: number
  /** Поиск по заголовку на обоих языках — только в админке. */
  q?: string
}

/** Ровно то, что экраны ждут от источника новостей — mock или API. */
export interface NewsBackend {
  list: (query?: NewsQuery) => Promise<NewsPage<NewsDto>>
  get: (id: number) => Promise<NewsDto>
  create: (input: NewsInput) => Promise<NewsDto>
  update: (id: number, input: NewsInput) => Promise<NewsDto>
  remove: (id: number) => Promise<void>
  upload: (file: Blob) => Promise<NewsMediaDto>
  /** Витрина: `GET /news`. */
  published: (query?: NewsQuery) => Promise<NewsPage<PublicNewsItemDto>>
  /** Витрина: `GET /news/{id}`. */
  publishedOne: (id: number) => Promise<PublicNewsDto>
}

export const MOCK_NEWS_KEY = 'dorixona.mock.news.v1'

export interface MockNewsOptions {
  storage: Pick<Storage, 'getItem' | 'setItem'>
  /** Часы приложения (мс). */
  now?: () => number
}

const invalid = (message: string) =>
  new ApiError({ code: 'validation_failed', status: 400, message })

/** Видимый текст HTML — пустой ли текст новости на самом деле. */
const visibleText = (html: string) =>
  html
    .replace(/<img\b[^>]*>/gi, 'img')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim()

/** Те же правила, что должен держать сервер (docs/notes/news-contract.md). */
export function validateNews(input: NewsInput): void {
  if (!input.title_ru.trim()) throw invalid('Нужен заголовок на русском')
  if (input.title_ru.length > NEWS_TITLE_MAX || input.title_uz.length > NEWS_TITLE_MAX) {
    throw invalid(`Заголовок длиннее ${NEWS_TITLE_MAX} символов`)
  }
  if (input.lead_ru.length > NEWS_LEAD_MAX || input.lead_uz.length > NEWS_LEAD_MAX) {
    throw invalid(`Анонс длиннее ${NEWS_LEAD_MAX} символов`)
  }
  if (input.body_ru.length > NEWS_BODY_MAX || input.body_uz.length > NEWS_BODY_MAX) {
    throw invalid('Текст длиннее 100 000 знаков')
  }
  if (input.is_published) {
    if (!input.photo_key) throw invalid('Без фото новость не опубликовать')
    if (!visibleText(input.body_ru)) throw invalid('Без текста на русском новость не опубликовать')
    if (!input.published_at) throw invalid('Нужна дата публикации')
  }
}

export function isPublic(news: NewsDto, now: number): boolean {
  return news.is_published && news.published_at !== null && Date.parse(news.published_at) <= now
}

/** Новые сверху: по дате публикации, черновики без даты — по дате создания. */
const newest = (a: NewsDto, b: NewsDto) =>
  (b.published_at ?? b.created_at).localeCompare(a.published_at ?? a.created_at) || b.id - a.id

export function toPublicItem(news: NewsDto): PublicNewsItemDto {
  return {
    id: news.id,
    title_ru: news.title_ru,
    title_uz: news.title_uz,
    lead_ru: news.lead_ru,
    lead_uz: news.lead_uz,
    photo_url: news.photo_url,
    published_at: news.published_at as string,
  }
}

function paginate<T>(items: T[], query: NewsQuery = {}): NewsPage<T> {
  const size = query.pageSize ?? NEWS_PAGE_SIZE
  const page = query.page ?? 1
  return { items: items.slice((page - 1) * size, page * size), total: items.length }
}

export function createMockNewsStore({
  storage,
  now = () => Date.now(),
}: MockNewsOptions): NewsBackend {
  function read(): NewsDto[] {
    try {
      const raw = storage.getItem(MOCK_NEWS_KEY)
      const parsed: unknown = raw === null ? null : JSON.parse(raw)
      return Array.isArray(parsed) ? (parsed as NewsDto[]) : structuredClone(SEED_NEWS)
    } catch {
      return structuredClone(SEED_NEWS)
    }
  }

  function write(list: NewsDto[]): void {
    try {
      storage.setItem(MOCK_NEWS_KEY, JSON.stringify(list))
    } catch {
      throw new ApiError({
        code: 'payload_too_large',
        status: 413,
        message: 'Хранилище браузера переполнено: удалите лишние новости или фото',
      })
    }
  }

  const notFound = () =>
    new ApiError({ code: 'not_found', status: 404, message: 'Новость не найдена' })

  function find(list: NewsDto[], id: number): NewsDto {
    const news = list.find((n) => n.id === id)
    if (!news) throw notFound()
    return news
  }

  function build(input: NewsInput, id: number, base: NewsDto | null): NewsDto {
    const stamp = new Date(now()).toISOString()
    return {
      id,
      title_ru: input.title_ru.trim(),
      title_uz: input.title_uz.trim(),
      lead_ru: input.lead_ru.trim(),
      lead_uz: input.lead_uz.trim(),
      body_ru: input.body_ru,
      body_uz: input.body_uz,
      photo_key: input.photo_key,
      photo_url: input.photo_key,
      is_published: input.is_published,
      published_at: input.published_at,
      created_at: base?.created_at ?? stamp,
      updated_at: stamp,
    }
  }

  const reply = <T>(work: () => T) =>
    new Promise<T>((resolve) => {
      resolve(work())
    })

  return {
    list: (query) =>
      reply(() => {
        const q = query?.q?.trim().toLowerCase() ?? ''
        const all = read()
          .filter(
            (n) =>
              !q || n.title_ru.toLowerCase().includes(q) || n.title_uz.toLowerCase().includes(q),
          )
          .sort(newest)
        return paginate(all, query)
      }),

    get: (id) => reply(() => find(read(), id)),

    create: (input) =>
      reply(() => {
        validateNews(input)
        const list = read()
        const news = build(input, Math.max(0, ...list.map((n) => n.id)) + 1, null)
        write([...list, news])
        return news
      }),

    update: (id, input) =>
      reply(() => {
        validateNews(input)
        const list = read()
        const news = build(input, id, find(list, id))
        write(list.map((n) => (n.id === id ? news : n)))
        return news
      }),

    remove: (id) =>
      reply(() => {
        const list = read()
        find(list, id)
        write(list.filter((n) => n.id !== id))
      }),

    upload: async (file) => {
      if (file.size > NEWS_MEDIA_MAX_BYTES) {
        throw new ApiError({ code: 'payload_too_large', status: 413, message: 'Файл больше 5 МБ' })
      }
      if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
        throw invalid('Нужна картинка JPEG, PNG или WebP')
      }
      const url = await toDataUrl(file)
      return { key: url, url }
    },

    published: (query) =>
      reply(() => {
        const at = now()
        return paginate(
          read()
            .filter((n) => isPublic(n, at))
            .sort(newest)
            .map(toPublicItem),
          query,
        )
      }),

    publishedOne: (id) =>
      reply(() => {
        const news = read().find((n) => n.id === id && isPublic(n, now()))
        if (!news) throw notFound()
        return { ...toPublicItem(news), body_ru: news.body_ru, body_uz: news.body_uz }
      }),
  }
}
